import React, { useState, useEffect } from 'react';
import { Question, UserAnswer } from '../types';
import { useDisplayTranslation } from '../hooks/useDisplayTranslation';

interface QuestionCardProps {
    question: Question;
    userAnswer: UserAnswer | undefined;
    onAnswerSelect: (questionId: number, selectedOptions: string[]) => void;
    isMarkedForReview: boolean;
    onToggleReview: (questionId: number) => void;
    isExamMode: boolean;
    displayLanguage: 'pt' | 'en' | 'es';
}

const QuestionCard: React.FC<QuestionCardProps> = ({ question, userAnswer, onAnswerSelect, isMarkedForReview, onToggleReview, isExamMode, displayLanguage }) => {
    const { stem, options: displayOptions, explanation: locExpl, loading: trLoading } = useDisplayTranslation(question, displayLanguage);
    const [selected, setSelected] = useState<string[]>([]);
    const isCheckedInPracticeMode = userAnswer !== undefined && !isExamMode;

    useEffect(() => {
        setSelected(userAnswer?.selected || []);
    }, [question.id, userAnswer]);

    const handleSelection = (optionKey: string) => {
        if (isCheckedInPracticeMode) return;
        
        let newSelection: string[];
        if (question.isMultiChoice) {
            newSelection = selected.includes(optionKey)
                ? selected.filter(item => item !== optionKey)
                : [...selected, optionKey];
        } else {
            newSelection = [optionKey];
        }
        setSelected(newSelection);
        
        if (isExamMode) {
            onAnswerSelect(question.id, newSelection);
        }
    };

    const handleCheckAnswer = () => {
        onAnswerSelect(question.id, selected);
    };
    
    const getOptionClass = (optionKey: string) => {
        const isCurrentlySelected = selected.includes(optionKey);

        if (isExamMode) {
            return `bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 ${isCurrentlySelected ? 'ring-2 ring-cyan-400' : ''}`;
        }

        if (!isCheckedInPracticeMode) {
            return `bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 ${isCurrentlySelected ? 'ring-2 ring-orange-400' : ''}`;
        }

        const isCorrect = question.correctAnswers.includes(optionKey);
        const isSelectedByUser = userAnswer?.selected.includes(optionKey);

        if (isCorrect) return 'bg-green-100 dark:bg-green-800/50 border-green-400 dark:border-green-500';
        if (isSelectedByUser && !isCorrect) return 'bg-red-100 dark:bg-red-800/50 border-red-400 dark:border-red-500';
        
        return 'bg-gray-100 dark:bg-slate-700';
    };
    
    const choiceStyle = question.isMultiChoice ? 'rounded-md' : 'rounded-full';

    return (
        <div className="bg-white dark:bg-slate-800 p-6 sm:p-8 rounded-lg shadow-2xl mb-6 animate-fade-in relative">
            {trLoading && displayLanguage !== 'pt' && (
                <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/70 dark:bg-slate-900/70 backdrop-blur-[1px]">
                    <span className="text-sm text-gray-600 dark:text-slate-300">
                        {displayLanguage === 'en' ? 'Translating…' : displayLanguage === 'es' ? 'Traduciendo…' : '…'}
                    </span>
                </div>
            )}
            {question.imgLink && (
                <div className="mb-4">
                    <img src={question.imgLink} alt={displayLanguage === 'en' ? `Question image ${question.id}` : displayLanguage === 'es' ? `Imagen de pregunta ${question.id}` : `Imagem da questão ${question.id}`} className="max-h-64 w-auto mx-auto rounded border border-gray-200 dark:border-slate-700" />
                </div>
            )}
            <div className="flex justify-end items-start mb-4">
                 <button onClick={() => onToggleReview(question.id)} className={`transition-colors text-sm px-3 py-1 rounded-full ${isMarkedForReview ? 'bg-yellow-500 text-black' : 'bg-gray-200 hover:bg-gray-300 text-gray-700 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-300'}`}>
                    <i className="far fa-flag mr-2"></i>{isMarkedForReview ? (displayLanguage === 'en' ? 'Marked' : displayLanguage === 'es' ? 'Marcada' : 'Marcada') : (displayLanguage === 'en' ? 'Mark for Review' : displayLanguage === 'es' ? 'Marcar para revisión' : 'Marcar para Revisão')}
                 </button>
            </div>
            <div
                className="prose prose-lg dark:prose-invert text-gray-700 dark:text-slate-300 mb-6"
                dangerouslySetInnerHTML={{ __html: stem }}
            />
            
            <div className="space-y-3">
                {Object.entries(displayOptions).map(([key, value]) => (
                    <div
                        key={key}
                        onClick={() => handleSelection(key)}
                        className={`p-4 border border-gray-300 dark:border-slate-600 rounded-lg cursor-pointer transition-all ${getOptionClass(key)}`}
                    >
                        <label className="flex items-center cursor-pointer">
                            <div className={`w-6 h-6 border-2 border-gray-400 dark:border-slate-500 ${choiceStyle} flex-shrink-0 flex items-center justify-center mr-4`}>
                                {selected.includes(key) && <i className={`fas fa-check ${isExamMode ? 'text-cyan-400' : 'text-orange-400'}`}></i>}
                            </div>
                            <span className="font-bold mr-2 text-orange-400">{key}.</span>
                            <span dangerouslySetInnerHTML={{ __html: value }} />
                        </label>
                    </div>
                ))}
            </div>

            <div className="mt-6 text-center">
                 {!isCheckedInPracticeMode && !isExamMode && (
                    <button
                        onClick={handleCheckAnswer}
                        disabled={selected.length === 0}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition-colors disabled:bg-slate-600 disabled:cursor-not-allowed"
                    >
                        {displayLanguage === 'en' ? 'Check Answer' : displayLanguage === 'es' ? 'Verificar respuesta' : 'Verificar Resposta'}
                    </button>
                )}
            </div>

            {isCheckedInPracticeMode && (
                <div className="mt-6 p-4 bg-gray-50 dark:bg-slate-900/50 rounded-lg border border-gray-200 dark:border-slate-700 animate-fade-in">
                    <h3 className="text-xl font-bold text-orange-400 mb-3">{displayLanguage === 'en' ? 'Explanation' : displayLanguage === 'es' ? 'Explicación' : 'Explicação'}</h3>
                    <p className="text-gray-700 dark:text-slate-300 mb-4">{locExpl.general}</p>
                    <div className="space-y-3">
                        {Object.entries(locExpl.options || {}).map(([key, expl]) => {
                             const isCorrect = question.correctAnswers.includes(key);
                             const icon = isCorrect ? <i className="fas fa-check-circle text-green-500 mr-2"></i> : <i className="fas fa-times-circle text-red-500 mr-2"></i>;
                            return (
                                <div key={key} className="text-gray-600 dark:text-slate-400">
                                   <p><strong>{displayLanguage === 'en' ? 'Option' : displayLanguage === 'es' ? 'Opción' : 'Opção'} {key}:</strong> {icon} {expl}</p>
                                </div>
                            )
                        })}
                    </div>
                     {question.docLink && (
                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-700">
                            <h4 className="text-lg font-semibold text-cyan-400 mb-2">{displayLanguage === 'en' ? 'Official Source' : displayLanguage === 'es' ? 'Fuente oficial' : 'Fonte Oficial'}</h4>
                            <a 
                                href={question.docLink} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-cyan-400 hover:text-cyan-300 hover:underline transition-colors"
                            >
                                {displayLanguage === 'en' ? 'Read documentation' : displayLanguage === 'es' ? 'Leer documentación' : 'Ler a documentação'} <i className="fas fa-external-link-alt ml-1 text-xs"></i>
                            </a>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default QuestionCard;
